import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Download, Book, Globe } from 'lucide-react';

const HeroContainer = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 2rem;
  margin-top: 80px;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    top: -50%;
    left: -50%;
    background: conic-gradient(
      from 0deg at 50% 50%,
      rgba(57, 192, 237, 0.1) 0deg,
      rgba(122, 67, 229, 0.1) 60deg,
      rgba(57, 192, 237, 0.05) 120deg,
      rgba(122, 67, 229, 0.05) 180deg,
      rgba(57, 192, 237, 0.1) 240deg,
      rgba(122, 67, 229, 0.1) 300deg,
      rgba(57, 192, 237, 0.1) 360deg
    );
    animation: rotate 30s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 50%, transparent 0%, #0e0e10 70%);
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Content = styled.div`
  text-align: center;
  z-index: 1;
  max-width: 800px;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #39c0ed, #7a43e5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  color: #b0b0b0;
  margin-bottom: 3rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(45deg, #39c0ed, #7a43e5);
  color: white;
  padding: 1rem 2rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(57, 192, 237, 0.5);
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: #39c0ed;
  padding: 1rem 2rem;
  border: 2px solid #39c0ed;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s;
  
  &:hover {
    background: rgba(57, 192, 237, 0.1);
    transform: translateY(-3px);
  }
`;

const Hero: React.FC = () => {
  return (
    <HeroContainer>
      <BackgroundGradient />
      <Content>
        <Title
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Contact Manager
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Organize. Connect. Simplify.
          <br />
          Your professional contact management solution
        </Subtitle>
        <ButtonGroup>
          <PrimaryButton
            href="https://github.com/Mrtracker-new/Contact-manager/releases/download/v2.0/Contact-Manager.apk"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Download size={20} />
            Download for Android
          </PrimaryButton>
          <SecondaryButton
            href="https://contact-manager-rnr.vercel.app/"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Globe size={20} />
            Try Web Version
          </SecondaryButton>
          <SecondaryButton
            href="https://github.com/Mrtracker-new/Contact-manager/wiki"
            target="_blank"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Book size={20} />
            Documentation
          </SecondaryButton>
        </ButtonGroup>
      </Content>
    </HeroContainer>
  );
};

export default Hero;
